// 새 평가 폼의 4축(규모·분류·단계·업종) 상호제약 엔진(렌더와 분리한 단일 훅).
//
// 실존 조합 매트릭스(GET /assessments/combinations)를 앵커로, 업종 선택에 맞춰
// 규모·분류·단계의 활성값을 계산하고 충돌 시 종속 축을 유효값으로 자동 보정한다(동작 보존).
// 렌더 컴포넌트(NewAssessmentForm)는 이 훅이 돌려주는 값·세터만 쓴다.

import { useEffect, useMemo, useState } from 'react'
import { listCombinations } from '~/api/assessment'
import type {
    AssessmentClassification,
    AssessmentCombination,
    AssessmentScale,
    BusinessContext,
} from '~/api/types'

type ComboAxis = 'scale' | 'classification' | 'assessmentType' | 'industry'
const COMBO_AXES: ComboAxis[] = [
    'scale',
    'classification',
    'assessmentType',
    'industry',
]

/**
 * combos 중 `axis` 이외의 축이 `sel`과 일치하는(빈 sel 값은 '아무거나') 항목들의 `axis` 값 집합.
 * 4축 상호제약(cascading)의 핵심: 나머지 선택을 고정했을 때 실존하는 axis 값만 남긴다.
 */
function optionsFor(
    combos: AssessmentCombination[],
    axis: ComboAxis,
    sel: Record<ComboAxis, string>
): string[] {
    const vals = new Set<string>()
    for (const c of combos) {
        const ok = COMBO_AXES.every(
            (a) => a === axis || !sel[a] || (c[a] as string) === sel[a]
        )
        if (ok) vals.add(c[axis] as string)
    }
    return [...vals]
}

export function useAssessmentCombinations(
    activeProfile?: BusinessContext | null
) {
    const [scale, setScale] = useState<AssessmentScale>('일반')
    const [industry, setIndustry] = useState('')
    const [profileTitle, setProfileTitle] = useState('내 사업장')
    const [classification, setClassification] =
        useState<AssessmentClassification>('선행요건')
    const [stage, setStage] = useState<string>('인증')
    const [combos, setCombos] = useState<AssessmentCombination[]>([])

    const ready = combos.length > 0

    // 현재 업종이 매트릭스에 없으면(미선택·과거 자유입력) 라디오 제약을 걸지 않는다.
    const knownIndustry = useMemo(
        () => (combos.some((c) => c.industry === industry) ? industry : ''),
        [combos, industry]
    )

    // 업종은 앵커: 전체 합집합에서 자유 선택. 저장된 값이 목록에 없으면 맨 앞에 보존한다.
    const industryOptions = useMemo(() => {
        const labels = [...new Set(combos.map((c) => c.industry))].sort(
            (a, b) => a.localeCompare(b, 'ko')
        )
        const withCurrent =
            industry && !labels.includes(industry)
                ? [industry, ...labels]
                : labels
        return withCurrent.map((l) => ({ value: l, label: l }))
    }, [combos, industry])

    // 규모·분류·단계 각 축에서 (선택 업종 + 나머지 축)과 실존 조합이 있는 값만 활성.
    const enabled = useMemo(() => {
        const sel: Record<ComboAxis, string> = {
            scale,
            classification,
            assessmentType: stage,
            industry: knownIndustry,
        }
        return {
            scale: new Set(optionsFor(combos, 'scale', sel)),
            classification: new Set(optionsFor(combos, 'classification', sel)),
            stage: new Set(optionsFor(combos, 'assessmentType', sel)),
        }
    }, [combos, scale, classification, stage, knownIndustry])

    // 마운트 시 실존 조합 매트릭스 로드(실패는 무시 — 제약 없이 자유 선택으로 폴백).
    useEffect(() => {
        let cancelled = false
        listCombinations()
            .then((rows) => {
                if (!cancelled) setCombos(rows)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!activeProfile) return
        if (activeProfile.scale) setScale(activeProfile.scale)
        setIndustry(activeProfile.industry ?? '')
        setProfileTitle(activeProfile.title || '내 사업장')
    }, [activeProfile])

    // 선택한 업종과 규모·분류·단계가 어긋나면 종속 축을 유효값으로 자동 보정(고정점까지).
    // 업종이 미선택/미상이면 제약이 없어 보정하지 않는다.
    useEffect(() => {
        if (!ready || !knownIndustry) return
        let s: Record<ComboAxis, string> = {
            scale,
            classification,
            assessmentType: stage,
            industry: knownIndustry,
        }
        for (let i = 0; i < 6; i += 1) {
            let changed = false
            for (const axis of [
                'scale',
                'classification',
                'assessmentType',
            ] as const) {
                const opts = optionsFor(combos, axis, s)
                if (opts.length && !opts.includes(s[axis])) {
                    s = { ...s, [axis]: opts[0] }
                    changed = true
                }
            }
            if (!changed) break
        }
        if (s.scale !== scale) setScale(s.scale as AssessmentScale)
        if (s.classification !== classification)
            setClassification(s.classification as AssessmentClassification)
        if (s.assessmentType !== stage) setStage(s.assessmentType)
    }, [ready, combos, knownIndustry, scale, classification, stage])

    return {
        scale,
        setScale,
        industry,
        setIndustry,
        profileTitle,
        setProfileTitle,
        classification,
        setClassification,
        stage,
        setStage,
        ready,
        enabled,
        industryOptions,
    }
}
